/**
 * HandPanel — hand FAB + slide-up panel with card stacks and score.
 *
 * Faithful port of the canonical wireframe `.hand-fab` / `.hand-panel`
 * (docs/wireframes/celestia/03-screen.html, lines ~499–516).
 *
 * HIDDEN-INFO INVARIANT: only the LOCAL player's hand is rendered.
 * Other players' hand identities are null in ViewerState — never render them.
 */

import type { ViewerPlayerState } from "@cloudnest/celestia-engine";
import { useState } from "react";
import { cardLabel } from "../components/card-label";
import type { UseLocaleReturn } from "../i18n/useLocale";

interface Props {
  readonly me: ViewerPlayerState | null;
  readonly i18n: UseLocaleReturn;
}

/** Group card ids by type prefix for stacked display. */
function groupCards(hand: readonly string[]): Map<string, readonly string[]> {
  const groups = new Map<string, string[]>();
  for (const id of hand) {
    const parts = id.split("-");
    const key =
      parts[0] === "eq" && parts[1]
        ? `eq-${parts[1]}`
        : parts[0] === "pw" && parts[1]
          ? `pw-${parts[1]}`
          : (parts[0] ?? id);
    const arr = groups.get(key) ?? [];
    arr.push(id);
    groups.set(key, arr);
  }
  return groups;
}

function CardStack({
  cardIds,
  i18n,
}: {
  readonly cardIds: readonly string[];
  readonly i18n: UseLocaleReturn;
}) {
  const { t } = i18n;
  const first = cardIds[0];
  if (!first) return null;
  const label = cardLabel(first);
  const count = cardIds.length;
  const isPower = first.startsWith("pw-");
  const isTurbo = first.startsWith("turbo");
  const showShadow = count > 1;

  return (
    <div className="cstack">
      {showShadow && <span className="sc" />}
      <span className="cb">×{count}</span>
      {isPower ? (
        <div className="pw">
          <svg className="ico" aria-hidden="true">
            <use href={label.icon ? `#${label.icon}` : "#ic-blank"} />
          </svg>
          <span className="nm">{t(label.labelKey)}</span>
        </div>
      ) : (
        <div
          className={isTurbo ? "eq turbo" : "eq"}
          style={
            isTurbo
              ? undefined
              : ({
                  "--hz": `var(${label.colorVar})`,
                } as React.CSSProperties)
          }
        >
          {!isTurbo && label.icon && (
            <>
              <span className="bdg l">
                <svg className="ico" aria-hidden="true">
                  <use href={`#${label.icon}`} />
                </svg>
              </span>
              <span className="bdg r">
                <svg className="ico" aria-hidden="true">
                  <use href={`#${label.icon}`} />
                </svg>
              </span>
            </>
          )}
          {isTurbo && (
            <svg className="ico ctr" aria-hidden="true">
              <use href="#ic-turbo" />
            </svg>
          )}
          <span className="nm">{t(label.labelKey)}</span>
        </div>
      )}
    </div>
  );
}

// Need React import for JSX
import type React from "react";

export function HandPanel({ me, i18n }: Props) {
  const { t } = i18n;
  const [open, setOpen] = useState(false);

  const hand = me?.hand ?? null;
  const handCount = me?.handCount ?? 0;

  // Score from treasures (local player only)
  const treasures = me?.treasures ?? null;
  const treasureValues = treasures
    ? treasures
        .filter((id) => !id.includes("spyglass"))
        .map((id) => {
          const m = id.match(/\d+/);
          return m ? Number(m[0]) : 0;
        })
    : [];
  const hasSpyglass = treasures?.some((id) => id.includes("spyglass")) ?? false;
  const spyglassBonus = hasSpyglass ? 2 : 0;
  const total = treasureValues.reduce((sum, v) => sum + v, 0) + spyglassBonus;

  const groups = hand ? groupCards(hand) : new Map<string, string[]>();

  return (
    <>
      <button
        className={open ? "hand-fab on" : "hand-fab"}
        data-testid="celestia-hand-fab"
        aria-label={t("a11y_hand_fab")}
        aria-expanded={open}
        type="button"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="cbadge">{handCount}</span>
      </button>

      <section
        className={open ? "hand-panel open" : "hand-panel"}
        data-testid="celestia-hand-panel"
        aria-label={t("hand_panel_title")}
      >
        <div className="hph">
          <span>{t("hand_panel_title")}</span>
          <button
            className="x"
            data-testid="celestia-hand-panel-close"
            type="button"
            onClick={() => setOpen(false)}
          >
            {t("hand_panel_close")}
          </button>
        </div>

        {hand && hand.length > 0 ? (
          <div className="hstacks">
            {[...groups.entries()].map(([key, ids]) => (
              <CardStack key={key} cardIds={ids} i18n={i18n} />
            ))}
          </div>
        ) : (
          <div
            style={{ fontSize: 11, color: "var(--ink-soft)", padding: "4px 0" }}
          >
            손패 없음
          </div>
        )}

        <div className="sect">{t("hand_panel_score_section")}</div>
        <div className="score">
          <div className="row">
            <span>{t("hand_score_treasures")}</span>
            <span className="gem">
              {treasureValues.length > 0
                ? treasureValues.map((v) => `◆${v}`).join(" · ")
                : "—"}
            </span>
          </div>
          {hasSpyglass && (
            <div className="row">
              <span>망원경 보너스 (미사용)</span>
              <span className="gem">+{spyglassBonus}</span>
            </div>
          )}
          <div className="row tot">
            <span>{t("hand_score_total")}</span>
            <span>{total}</span>
          </div>
        </div>
      </section>
    </>
  );
}
